import { computed, type MaybeRefOrGetter, toValue } from "vue";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { buildMonths, formatAvailableTimes, toWeekdayIndexes } from "@/app/formatters/schedule";
import { appointmentDataSource, patientAppointmentDataSource, scheduleDataSource } from "@/di/datasources";

/** Meses do calendário com os dias da semana em que o psi atende. */
export function useAvailableMonths(psiId: MaybeRefOrGetter<number | undefined>, enabled: MaybeRefOrGetter<boolean> = true) {
  return useQuery({
    queryKey: computed(() => ["schedule", "days", toValue(psiId)]),
    enabled: computed(() => !!toValue(psiId) && toValue(enabled)),
    queryFn: async () => buildMonths(toWeekdayIndexes(await scheduleDataSource.getScheduleAvailableDays({ user_id: toValue(psiId) }))),
    staleTime: 0,
  });
}

/** Horários livres do psi no dia (só os futuros). */
export function useAvailableTimes(psiId: MaybeRefOrGetter<number | undefined>, date: MaybeRefOrGetter<string>) {
  return useQuery({
    queryKey: computed(() => ["schedule", "times", toValue(psiId), toValue(date)]),
    enabled: computed(() => !!toValue(psiId) && !!toValue(date)),
    queryFn: async () =>
      formatAvailableTimes(await scheduleDataSource.getScheduleAvailableTimes({ user_id: toValue(psiId), date: toValue(date) })),
    staleTime: 0,
  });
}

/** Horário avulso aberto por link (`/horario/:uuid`). */
export function useSharedSlot(uuid: MaybeRefOrGetter<string | undefined>) {
  return useQuery({
    queryKey: computed(() => ["schedule", "shared-slot", toValue(uuid)]),
    enabled: computed(() => !!toValue(uuid)),
    queryFn: () => patientAppointmentDataSource.getSharedSlot(toValue(uuid)!),
    retry: false,
  });
}

/** Pedir reagendamento (o psi precisa aceitar). */
export function useRequestReschedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: { appointmentId: number; newDate: string }) => {
      const res: any = await appointmentDataSource.createRescheduleRequest({ appointment_id: p.appointmentId, new_date: p.newDate });
      // o legado tratava { status: false, message } como erro
      if (res && res.status === false) throw new Error(res.message);
      return res;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["appointments"] }),
  });
}
